import { date, money } from '../format';
import { useJourney } from '../journey';
import { IRepaymentAccount } from '../types';
import { useOffer } from '../useOffer';
import Button from '../ui/Button';
import { Copyable, DetailRows, Loading, StatusCard } from '../ui/Parts';
import Shell from '../ui/Shell';

const CloseFooter = ({ label = 'Close' }: { label?: string }) => {
  const { close } = useJourney();
  return <Button onClick={close}>{label}</Button>;
};

const repaymentAccountRows = (account: IRepaymentAccount) => [
  { label: 'Repay into:', value: account.bankName ?? '' },
  { label: 'Account number:', value: <Copyable value={account.accountNumber} /> },
  ...(account.accountName ? [{ label: 'Account name:', value: account.accountName }] : []),
];

/**
 * The merchant is being paid and the repayment account opened; usually under a minute. Once the account is open the
 * customer gets it here too (it is also sent by email and SMS), so a slow payout to the merchant does not leave them
 * looking at a spinner; the screen moves to AllSet when the merchant is paid.
 */
export const SettingUpLoan = () => {
  const { session, close } = useJourney();
  const account = session.repaymentAccount;
  if (account) {
    return (
      <Shell onClose={close} footer={<CloseFooter />}>
        <StatusCard
          tone="info"
          title="Your loan is set up"
          subtitle="We're paying the merchant now. We'll email and text you as soon as they can release your purchase."
        >
          <DetailRows rows={repaymentAccountRows(account)} />
        </StatusCard>
      </Shell>
    );
  }
  return (
    <Shell onClose={close}>
      <div className="text-center py-6">
        <Loading />
        <h2 className="text-lg md:text-xl font-semibold">Setting up your loan</h2>
        <p className="mt-2 text-sm md:text-base text-lend-body">
          We&apos;re paying the merchant and opening your repayment account. This usually takes under a minute.
        </p>
      </div>
    </Shell>
  );
};

/** Funded: the merchant releases the purchase; the customer repays into their own account. */
export const AllSet = () => {
  const { session, close } = useJourney();
  const { offer } = useOffer(true);
  const account = session.repaymentAccount;
  const option = offer?.options.find((o) => o.tenor === offer.selectedTenor);
  const currency = offer?.currency ?? session.currency;
  const done = session.status === 'COMPLETED';
  return (
    <Shell onClose={close} footer={<CloseFooter label="Done" />}>
      <StatusCard
        tone="success"
        title={done ? 'Your loan is fully repaid' : "You're all set"}
        subtitle={done ? 'Thank you. Nothing more is owed.' : 'Your loan is active and the merchant has been paid, so they can release your purchase.'}
      >
        {!done && (
          <DetailRows
            rows={[
              ...(offer ? [{ label: 'Loan amount:', value: money(offer.financedAmount, currency) }] : []),
              ...(option
                ? [
                    { label: 'Each repayment:', value: money(option.instalmentAmount, currency) },
                    { label: 'First repayment date:', value: date(option.firstRepaymentDate) },
                  ]
                : []),
              ...(account
                ? repaymentAccountRows(account)
                : [{ label: 'Repayment account:', value: 'We will email and text you the details' }]),
            ]}
          />
        )}
      </StatusCard>
    </Shell>
  );
};

export const NotEligible = () => {
  const { session, close } = useJourney();
  return (
    <Shell onClose={close} footer={<CloseFooter />}>
      <StatusCard
        tone="error"
        title="You're not eligible for this loan"
        subtitle={session.eligibility?.message ?? 'We are unable to offer you financing for this purchase.'}
      />
    </Shell>
  );
};

/** Eligible for a loan, but not one large enough for this purchase after the upfront payment. */
export const ChooseCheaperProduct = () => {
  const { session, close } = useJourney();
  const { maxPurchaseAmount, maxFinancedAmount } = session.eligibility ?? {};
  return (
    <Shell onClose={close} footer={<CloseFooter label="Choose a different product" />}>
      <StatusCard
        tone="info"
        title="Choose a different product"
        subtitle="This purchase is more than you can finance. You can still buy a cheaper product in instalments; your checks and statement will be reused."
      >
        <DetailRows
          rows={[
            ...(maxPurchaseAmount !== undefined ? [{ label: 'Most you can spend:', value: money(maxPurchaseAmount, session.currency) }] : []),
            ...(maxFinancedAmount !== undefined ? [{ label: 'Most you can finance:', value: money(maxFinancedAmount, session.currency) }] : []),
          ]}
        />
      </StatusCard>
    </Shell>
  );
};

const CLOSED: Record<string, { title: string; subtitle: string }> = {
  VERIFICATION_FAILED: {
    title: "We couldn't verify your details",
    subtitle: 'Your identity details did not match our checks, so we cannot continue with this application.',
  },
  OFFER_REJECTED: { title: 'You declined the offer', subtitle: 'No payment has been taken. You can close this window.' },
  CANCELLED: { title: 'This application was cancelled', subtitle: 'No further action is needed.' },
  FAILED: { title: 'We could not complete this application', subtitle: 'Please contact the merchant if you have already paid.' },
};

export const Closed = () => {
  const { session, close } = useJourney();
  const copy = CLOSED[session.status] ?? CLOSED.FAILED;
  return (
    <Shell onClose={close} footer={<CloseFooter />}>
      <StatusCard tone={session.status === 'OFFER_REJECTED' ? 'info' : 'error'} title={copy.title} subtitle={copy.subtitle} />
    </Shell>
  );
};

/** Outside the journey context: the session could not be loaded at all. */
export const SessionProblem = ({ message, onClose }: { message: string; onClose: () => void }) => (
  <Shell onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
    <StatusCard tone="error" title="We couldn't open your application" subtitle={message} />
  </Shell>
);

export const Starting = ({ onClose }: { onClose: () => void }) => (
  <Shell onClose={onClose}>
    <Loading label="Loading your application…" />
  </Shell>
);
